// The lands down the left: one button per land, kept level with its lane as lanes grow and shrink. Each says
// who ruled it in the year you are looking at. Click one to bring that land's whole history into view.

import type { Chart, Realm } from "@hoee/chart";
import { DAY, NIGHT } from "../theme.ts";
import type { Camera } from "../view/camera.ts";

interface Row {
  el: HTMLElement;
  button: HTMLButtonElement;
  ruler: HTMLElement;
  swatch: HTMLElement;
  shown: { y: number; h: number; ruler: string; size: string; active: boolean; colour: string };
}

const css = (c: readonly number[]) => `rgb(${c.map((v) => Math.round(v * 255)).join(" ")})`;

export class LandsPanel {
  readonly el: HTMLElement;
  private readonly rows: Row[];
  private readonly chart: Chart;
  private readonly onOpen: (realm: Realm) => void;
  /** The list of a focused land's realms, built when a land comes forward. */
  private list: { lane: number; el: HTMLElement; items: Map<Realm, HTMLElement>; now: Realm | null } | null = null;

  constructor(chart: Chart, onPick: (lane: number) => void, onOpen: (realm: Realm) => void) {
    this.chart = chart;
    this.onOpen = onOpen;
    this.el = document.createElement("nav");
    this.el.className = "lands";
    this.el.setAttribute("aria-label", "Lands");
    this.rows = chart.columns.map((c, lane) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "land";
      const swatch = document.createElement("span");
      swatch.className = "land-swatch";
      const name = document.createElement("span");
      name.className = "land-name";
      name.textContent = c.name;
      const ruler = document.createElement("span");
      ruler.className = "land-ruler";
      button.append(swatch, name, ruler);
      button.addEventListener("click", () => onPick(lane));
      const el = document.createElement("div");
      el.className = "land-row";
      el.append(button);
      this.el.append(el);
      return { el, button, ruler, swatch, shown: { y: -1, h: -1, ruler: "", size: "", active: false, colour: "" } };
    });
  }

  /** Every realm that held a land, in order: its story at a glance, each one a way in. */
  private buildList(lane: number): void {
    this.list?.el.remove();
    const blocks = this.chart.blocks.filter((b) => Math.floor(b.x0) === lane).sort((a, b) => a.cy0 - b.cy0);
    const seen = new Map<Realm, { from: number; to: number; partial: boolean }>();
    for (const b of blocks) {
      const r = seen.get(b.realm);
      if (r) {
        r.from = Math.min(r.from, b.cy0);
        r.to = Math.max(r.to, b.y1);
        r.partial &&= !!b.partial;
      } else seen.set(b.realm, { from: b.cy0, to: b.y1, partial: !!b.partial });
    }
    const el = document.createElement("ol");
    el.className = "land-realms";
    el.setAttribute("aria-label", `Realms of ${this.chart.columns[lane]?.name ?? ""}`);
    const items = new Map<Realm, HTMLElement>();
    const now = new Date().getFullYear();
    for (const [realm, span] of seen) {
      const li = document.createElement("li");
      const b = document.createElement("button");
      b.type = "button";
      const name = document.createElement("span");
      name.textContent = realm.name;
      const years = document.createElement("small");
      const fmt = (y: number) => (y < 0 ? `${-y} BC` : y < 1000 ? `AD ${y}` : `${y}`);
      years.textContent = `${realm.fades && span.from === realm.start ? "before " : ""}${fmt(span.from)} – ${span.to >= now ? "today" : fmt(span.to)}${span.partial ? " · in part" : ""}`;
      b.append(name, years);
      b.addEventListener("click", () => this.onOpen(realm));
      li.append(b);
      el.append(li);
      items.set(realm, b);
    }
    (this.rows[lane] as Row).el.append(el);
    this.list = { lane, el, items, now: null };
  }

  /** Places every row beside its lane and says who held the land in `year`. Writes only what changed. */
  update(camera: Camera, rulerOf: (lane: number) => Realm | null, active: number | null, night: boolean): void {
    const pigments = night ? NIGHT.pigments : DAY.pigments;
    if (active == null && this.list) {
      this.list.el.remove();
      this.list = null;
    }
    // Rows follow their lanes, but never get too thin to read: a narrowed land keeps a readable row, and the
    // land in front gives up the difference, so the list always shows every land in order.
    // The land in front keeps room for its list of realms; when the others can't all keep a full row beside
    // it, they close up to compact rows (still readable) before it gives up any more.
    const lanes = this.rows.map((_, lane) => Math.max(0, camera.yOf(lane + 1) - camera.yOf(lane)));
    const total = lanes.reduce((a, b) => a + b, 0);
    const front = active == null ? 0 : Math.min(lanes[active] ?? 0, total * 0.42);
    const others = this.rows.length - (active == null ? 0 : 1);
    const MIN = Math.max(16, Math.min(24, (total - front) / Math.max(1, others)));
    const floor = (lane: number) => (lane === active ? Math.max(MIN, front) : MIN);
    const short = lanes.reduce((n, h, lane) => n + Math.max(0, floor(lane) - h), 0);
    const roomy = lanes.reduce((n, h, lane) => n + Math.max(0, h - floor(lane)), 0);
    const give = roomy > 0 ? Math.min(1, short / roomy) : 0;
    let y = camera.yOf(0);
    const heights = lanes.map((h, lane) => {
      const f = floor(lane);
      return h < f ? f : h - (h - f) * give;
    });
    const scale = total > 0 ? total / heights.reduce((a, b) => a + b, 0) : 1;
    this.rows.forEach((row, lane) => {
      const h = Math.round((heights[lane] as number) * scale * 2) / 2;
      const top = Math.round(y * 2) / 2;
      y += (heights[lane] as number) * scale;
      const s = row.shown;
      if (top !== s.y || h !== s.h) {
        row.el.style.transform = `translateY(${top}px)`;
        row.el.style.height = `${h}px`;
        s.y = top;
        s.h = h;
      }
      // Thin rows keep only their colour; short ones their name; tall ones say who ruled.
      const size = h < 15 ? "thin" : h < 21 ? "compact" : h < 40 ? "short" : "tall";
      if (size !== s.size) {
        row.el.dataset.size = size;
        s.size = size;
      }
      const realm = rulerOf(lane);
      const text = realm?.name ?? "";
      if (text !== s.ruler) {
        row.ruler.textContent = text;
        row.button.title = realm ? `${row.button.querySelector(".land-name")?.textContent}: ${text}` : "";
        s.ruler = text;
      }
      const colour = realm ? css(pigments[realm.color] ?? [0.5, 0.5, 0.5]) : "transparent";
      if (colour !== s.colour) {
        row.swatch.style.background = colour;
        s.colour = colour;
      }
      const isActive = active === lane;
      if (isActive && this.list?.lane !== lane) this.buildList(lane);
      if (isActive && this.list && this.list.now !== realm) {
        // The realm ruling now is lit, and the list scrolls to keep it in sight.
        const list = this.list;
        list.now = realm;
        for (const [r, item] of list.items) item.classList.toggle("now", r === realm);
        const item = realm ? list.items.get(realm) : undefined;
        if (item)
          list.el.scrollTo({
            top: Math.max(0, item.offsetTop - list.el.clientHeight / 2 + item.offsetHeight / 2),
            behavior: "smooth",
          });
      }
      if (isActive !== s.active) {
        row.el.classList.toggle("active", isActive);
        row.button.setAttribute("aria-pressed", String(isActive));
        s.active = isActive;
      }
    });
  }
}
